import { Text } from '@mantine/core';
import { useQueries } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import {
	createFacetedMinMaxValues,
	createFacetedRowModel,
	createFilteredRowModel,
	createSortedRowModel,
	useTable,
} from '@tanstack/react-table';

import { AddIcon } from '@apple/assets/icons';
import { useTranslation } from '@apple/lib/i18next';
import { useCurrentLocation } from '@apple/lib/tanstack-router/hooks/use-router-history';
import { DataTable } from '@apple/ui/data-table/controls/data-table';
import { ToolbarButton } from '@apple/ui/data-table/controls/Toolbar/toolbar-button';
import { createDataTableFeatures } from '@apple/ui/data-table/features';
import { useTableState } from '@apple/ui/data-table/hooks/use-table-state';
import { TableLayout } from '@apple/ui/layouts/TableLayout';
import { Link } from '@apple/ui/link/Link';

import { requireAuth } from '@apple/features/auth/utils/require-auth';
import type { Country, CountryFilters } from '@apple/features/country/models';
import { countryOdataQueryOptions } from '@apple/features/country/odata.queries';

const features = createDataTableFeatures({
	filteredRowModel: createFilteredRowModel(),
	sortedRowModel: createSortedRowModel(),
	facetedRowModel: createFacetedRowModel(),
	facetedMinMaxValues: createFacetedMinMaxValues(),
});

export const Route = createFileRoute('/_authed/_admin/manage/countries')({
	component: CountryManagementRoute,
	beforeLoad: args =>
		requireAuth(args, {
			requirePermission: 'Manzanita.Security.Features.CountryManagement.Read',
		}),
});

function CountryManagementRoute() {
	const { t } = useTranslation('manage');
	const navigate = Route.useNavigate();
	const location = useCurrentLocation();
	const search = Route.useSearch();
	const { auth } = Route.useRouteContext();

	const tableState = useTableState<Country, CountryFilters>({
		search,
		navigate,
		defaultSorting: [{ id: 'name', desc: true }],
		fieldMap: [],
	});

	const [customerSearchQuery] = useQueries({
		queries: [
			countryOdataQueryOptions({
				pagination: tableState.state.pagination,
				filters: tableState.currentFilterData,
			}),
		],
	});

	const table = useTable({
		features,
		data: customerSearchQuery.data.rows,
		rowCount: customerSearchQuery.data.totalRowCount,
		...tableState,
		enableFilters: true,
		enableColumnFilters: true,
		enableSorting: true,
		manualFiltering: true,
		manualSorting: true,
		manualPagination: true,
		columnResizeMode: 'onChange',
		columns: [
			{
				accessorKey: 'name',
				size: 130,
				header: t('countries.fields.countryName'),
				filter: {
					group: t('customers.filterGroups.details'),
				},
				cell: ({ row }) =>
					auth.hasPermission('Manzanita.Security.Features.CountryManagement.Write') ? (
						<Link
							to='/manage/countries/$countryCode'
							state={{ from: location }}
							params={{ countryCode: row.original.code }}
							title={row.original.name}
							children={row.original.name}
						/>
					) : (
						<Text>{row.original.name}</Text>
					),
			},
			{
				id: 'code',
				size: 90,
				enableColumnFilter: false,
				enableSorting: false,
				accessorKey: 'code',
				header: t('countries.fields.countryCode'),
				filter: {
					group: t('countries.filterGroups.details'),
				},
				cell: ({ row }) => <Text>{row.original.code}</Text>,
			},
			{
				accessorKey: 'cultureCode',
				enableColumnFilter: false,
				enableSorting: false,
				header: t('countries.fields.cultureCode'),
				size: 80,
				cell: ({ row }) => <Text>{row.original.cultureCode}</Text>,
			},
			{
				accessorKey: 'defaultPlant',
				enableSorting: false,
				header: t('countries.fields.defaultPlant'),
				size: 200,
				cell: ({ row }) => <Text>{row.original.plant?.name}</Text>,
				enableColumnFilter: false,
			},
			{
				accessorKey: 'currencyCode',
				enableSorting: false,
				header: t('countries.fields.defaultCurrency'),
				size: 75,
				enableColumnFilter: false,
			},
			{
				accessorKey: 'isEligibleForReturnOrders',
				enableSorting: false,
				header: t('countries.fields.isEligibleForReturnOrders'),
				size: 70,
				cell: ({ row }) => (
					<Text>{row.original.isEligibleForReturnOrders ? 'Yes' : 'No'}</Text>
				),
				enableColumnFilter: false,
			},
		],
	});

	return (
		<TableLayout
			title={t('countries.title')}
			table={table}
			toolbarButtons={[
				auth.hasPermission('Manzanita.Security.Features.CountryManagement.Write') && (
					<ToolbarButton
						key='add'
						tooltip={t('countries.buttons.add.tooltip')}
						icon={AddIcon}
						onClick={() =>
							void navigate({
								to: '/manage/countries/add',
								state: { from: location },
								viewTransition: false,
							})
						}
					/>
				),
			]}
		>
			<DataTable table={table} loading={customerSearchQuery.isFetching} />
		</TableLayout>
	);
}
